// Project: GalaxyComplete
// Created: 9/25/20 by sammy

import * as React from "react";
import { observer } from "mobx-react-lite";
import { Box, Card, CardContent, Chip, ListItemButton, ListItemIcon, ListItemText, Stack, Typography } from "@mui/material";
import { useCurrentProject, useCurrentProjectID } from "../project/CurrentProjectState";
import { useAppServices } from "../app/services";
import { useAppGlobalState } from "../app/AppGlobalState";
import { PROJECT_PRODUCT_TYPE, useProjectHasProductType, useProjectHasDatasyncEnabled, useProjectHasDisasterRecoveryEnabled } from "../project/project_hooks";
import { generatePath, Link, matchPath, useLocation } from "react-router-dom";
import {
    CMC_MIGRATION_SUBROUTE,
    CMO_MIGRATION_SUBROUTE,
    DISASTER_RECOVERY_SUBROUTE,
    getProjectSubRouteTemplate,
    INTEGRATIONS_SUBROUTE,
    MIGRATE_OPS_SUBROUTE,
    PROJECT_SUBROUTE,
} from "../app/AppRoutes";
import {
    AIAgentsIcon,
    ApiReferenceIcon,
    CmcHostsIcon,
    CmoSystemsIcon,
    DataProtectionIcon,
    DashboardIcon,
    GlobalMapViewIcon,
    IntegrationsIcon,
    MigrationIcon,
    ProjectSettingsIcon,
    UnstructuredDataIcon,
    UserCountIcon,
} from "../project/ProjectCommon";
import { FeatureFlag } from "../app/AppGlobalState";
import { getIsPrivateEdition, PrivateEditionView } from "../auth/PrivateEditionView";
import { ServerIcon } from "../../common/CommonIcons";
import { ProjectDetails } from "gc-web-proto/galaxycompletepb/apipb/domainpb/project_pb";
import { ReportIcon } from "../../common/CommonIcons";
import { useSideMenuListItemStyle } from "./LayoutCommon";
import { GoDotFill } from "react-icons/go";
import { useIsFeatureEnabled } from "../core/featureflag/FeatureFlags";
import { useTranslation } from "react-i18next";

interface CurrentProjectInfoProps {}

export const CurrentProjectSideMenu: React.FC<CurrentProjectInfoProps> = observer((p) => {
    const project = useCurrentProject();
    const hasCmc = useProjectHasProductType(PROJECT_PRODUCT_TYPE.CMC);
    const hasCmo = useProjectHasProductType(PROJECT_PRODUCT_TYPE.CMO);
    const hasDisasterRecovery = useProjectHasDisasterRecoveryEnabled();
    const cmoSupported = useIsFeatureEnabled(FeatureFlag.CMO_SUPPORT);

    if (!project) {
        return null;
    }

    return (
        <>
            <Box pb={1}>
                <OverviewSubMenu />
            </Box>
            {hasCmc && (
                <Box pb={1}>
                    <CmcMigrationSubMenu />
                </Box>
            )}
            {hasCmo && cmoSupported && (
                <Box pb={1}>
                    <CmoMigrationSubMenu />
                </Box>
            )}
            {hasCmc && hasDisasterRecovery && (
                <Box pb={1}>
                    <DisasterRecoverySubMenu />
                </Box>
            )}
            <Box pb={1}>
                <AutomationSubMenu />
            </Box>
            <Box pb={1}>
                <ManagementSubMenu />
            </Box>
        </>
    );
});

// ======================
// ProjectMenuHeader
// ======================

interface ProjectMenuHeaderProps {
    currentProject: ProjectDetails;
}

const ProjectMenuHeader: React.FC<ProjectMenuHeaderProps> = observer((p) => {
    const isPrivateEdition = getIsPrivateEdition();
    const { t } = useTranslation();

    if (isPrivateEdition) {
        return (
            <Box pb={2} pt={1} pl={2}>
                <Card
                    sx={{
                        border: `1px solid rgba(255,255,255,.2)`,
                    }}
                >
                    <CardContent>
                        <Typography variant={"overline"} color={"textSecondary"}>
                            {t("layout:currentProject")}
                        </Typography>
                        <Typography variant={"subtitle2"}>{p.currentProject.getInfo().getName()}</Typography>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    return (
        <Box p={2} pt={1}>
            <Card
                sx={{
                    border: `1px solid rgba(255,255,255,.2)`,
                }}
            >
                <Box pr={2} pb={1} pl={2} pt={1}>
                    <Typography variant={"overline"} color={"textSecondary"}>
                        {t("layout:currentProject")}
                    </Typography>
                    <Typography variant={"subtitle2"}>{p.currentProject.getInfo().getName()}</Typography>
                    <Stack direction={"row"} spacing={1} alignItems={"center"} pt={1}>
                        <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
                            <UserCountIcon size={14} />
                            <Typography>5</Typography>
                        </Stack>
                        <Chip size={"small"} label={t("layout:roles.admin")} />
                    </Stack>
                </Box>
            </Card>
        </Box>
    );
});

// ======================
// OverviewSubMenu
// ======================

interface OverviewSubMenuProps {}

export const OverviewSubMenu: React.FC<OverviewSubMenuProps> = (p) => {
    const projectId = useCurrentProjectID();
    const isPrivateEdition = getIsPrivateEdition();
    const { t } = useTranslation();
    return (
        <>
            <Stack pl={SUBTITLE_LEFT_PADDING} direction={"row"} spacing={1} alignItems={"center"}>
                <DashboardIcon size={ICON_SIZE} />
                <Typography color={"textPrimary"} variant={"overline"}>
                    {t("layout:navigation.overview")}
                </Typography>
            </Stack>
            <ProjectSubMenuListItem projectId={projectId} label={t("layout:navigation.dashboard")} route={PROJECT_SUBROUTE.DASHBOARD} />
            {!isPrivateEdition && (
                <ProjectSubMenuListItem
                    projectId={projectId}
                    icon={<GlobalMapViewIcon size={14} />}
                    label={t("layout:navigation.locations")}
                    route={PROJECT_SUBROUTE.GLOBAL_MAP_VIEW}
                />
            )}
        </>
    );
};

// ======================
// DataMigrationSubMenu
// ======================

interface CmcMigrationSubMenuProps {}

export const CmcMigrationSubMenu: React.FC<CmcMigrationSubMenuProps> = observer((p) => {
    const currentProject = useCurrentProject();
    const hasDataSync = useProjectHasDatasyncEnabled();
    const { t } = useTranslation();

    const projectId = currentProject.getInfo().getProjectId();
    return (
        <>
            <Stack pl={SUBTITLE_LEFT_PADDING} direction={"row"} spacing={1} alignItems={"center"}>
                <CmcHostsIcon size={ICON_SIZE} />
                <Typography color={"textPrimary"} variant={"overline"}>
                    {t("layout:navigation.hostBasedMigration")}
                </Typography>
            </Stack>
            <ProjectSubMenuListItem
                projectId={projectId}
                label={t("layout:navigation.registeredHosts")}
                route={`${PROJECT_SUBROUTE.CMC_MIGRATIONS}/${CMC_MIGRATION_SUBROUTE.DEPLOYMENTS}`}
            />
            <ProjectSubMenuListItem
                projectId={projectId}
                label={t("layout:navigation.h2hConnections")}
                route={`${PROJECT_SUBROUTE.CMC_MIGRATIONS}/${CMC_MIGRATION_SUBROUTE.CONNECTIONS}`}
            />
            <ProjectSubMenuListItem
                projectId={projectId}
                label={hasDataSync ? t("layout:navigation.blockMigrations") : t("layout:navigation.migrationSessions")}
                route={`${PROJECT_SUBROUTE.CMC_MIGRATIONS}/${CMC_MIGRATION_SUBROUTE.SESSIONS}`}
            />
            {hasDataSync && (
                <ProjectSubMenuListItem
                    projectId={projectId}
                    label={t("layout:navigation.unstructuredData")}
                    route={`${PROJECT_SUBROUTE.CMC_MIGRATIONS}/${CMC_MIGRATION_SUBROUTE.UNSTRUCTURED_DATA}`}
                />
            )}
        </>
    );
});

// ======================
// CmoMigrationSubMenu
// ======================

interface CmoMigrationSubMenuProps {}

export const CmoMigrationSubMenu: React.FC<CmoMigrationSubMenuProps> = (p) => {
    const currentProject = useCurrentProject();
    const projectId = currentProject.getInfo().getProjectId();
    const { t } = useTranslation();

    return (
        <>
            <Stack pl={SUBTITLE_LEFT_PADDING} direction={"row"} spacing={1} alignItems={"center"}>
                <CmoSystemsIcon size={14} />
                <Typography color={"textPrimary"} variant={"overline"}>
                    {t("layout:navigation.sanMigration")}
                </Typography>
            </Stack>
            <ProjectSubMenuListItem
                projectId={projectId}
                label={t("layout:navigation.cmcSummary")}
                route={`${PROJECT_SUBROUTE.CMO_MIGRATIONS}/${CMO_MIGRATION_SUBROUTE.DASHBOARD}`}
            />
            <ProjectSubMenuListItem
                projectId={projectId}
                label={t("layout:navigation.registeredAppliances")}
                route={`${PROJECT_SUBROUTE.CMO_MIGRATIONS}/${CMO_MIGRATION_SUBROUTE.CMO_SYSTEMS}`}
            />
            <ProjectSubMenuListItem
                projectId={projectId}
                label={t("layout:navigation.fcNexus")}
                route={`${PROJECT_SUBROUTE.CMO_MIGRATIONS}/${CMO_MIGRATION_SUBROUTE.NEXUS}`}
            />
            <ProjectSubMenuListItem
                projectId={projectId}
                label={t("layout:navigation.migrationSessions")}
                route={`${PROJECT_SUBROUTE.CMO_MIGRATIONS}/${CMO_MIGRATION_SUBROUTE.MIGRATION_SESSIONS}`}
            />
            <ProjectSubMenuListItem
                projectId={projectId}
                label={t("layout:navigation.systemWarnings")}
                route={`${PROJECT_SUBROUTE.CMO_MIGRATIONS}/${CMO_MIGRATION_SUBROUTE.WARNINGS}`}
            />
        </>
    );
};

// ======================
// DisasterRecoverySubMenu
// ======================

interface DisasterRecoverySubMenuProps {}

export const DisasterRecoverySubMenu: React.FC<DisasterRecoverySubMenuProps> = observer((p) => {
    const projectId = useCurrentProjectID();
    const { t } = useTranslation();

    return (
        <>
            <Stack pl={SUBTITLE_LEFT_PADDING} direction={"row"} spacing={1} alignItems={"center"}>
                <DataProtectionIcon fontSize={"small"} />
                <Typography color={"textPrimary"} variant={"overline"}>
                    {t("layout:navigation.disasterRecovery")}
                </Typography>
            </Stack>
            <ProjectSubMenuListItem
                projectId={projectId}
                label={t("layout:navigation.protectedHosts")}
                route={PROJECT_SUBROUTE.DISASTER_RECOVERY}
                linkSubpath={DISASTER_RECOVERY_SUBROUTE.PROTECTED_WORKLOADS}
                excludeSelectionSubpath={DISASTER_RECOVERY_SUBROUTE.SERVICE_LEVELS}
            />
            <ProjectSubMenuListItem
                projectId={projectId}
                label={t("layout:navigation.serviceLevels")}
                route={PROJECT_SUBROUTE.DISASTER_RECOVERY}
                linkSubpath={DISASTER_RECOVERY_SUBROUTE.SERVICE_LEVELS}
                selectionSubpath={DISASTER_RECOVERY_SUBROUTE.SERVICE_LEVELS}
            />
            <ProjectSubMenuListItem projectId={projectId} label={t("layout:navigation.dropsRecipes")} route={PROJECT_SUBROUTE.DR_DOCS} />
        </>
    );
});

// ======================
// AutomationSubMenu
// ======================

interface AutomationSubMenuProps {}

export const AutomationSubMenu: React.FC<AutomationSubMenuProps> = observer((p) => {
    const projectId = useCurrentProjectID();
    const { t } = useTranslation();

    return (
        <>
            <Stack pl={SUBTITLE_LEFT_PADDING} direction={"row"} spacing={1} alignItems={"center"}>
                <MigrationIcon size={ICON_SIZE} />
                <Typography color={"textPrimary"} variant={"overline"}>
                    {t("layout:navigation.automation")}
                </Typography>
            </Stack>
            <ProjectSubMenuListItem projectId={projectId} label={t("layout:navigation.migrateOps")} route={PROJECT_SUBROUTE.MIGRATE_OPS} />
            <ProjectSubMenuListItem projectId={projectId} label={t("layout:navigation.migrateOpsRecipes")} route={PROJECT_SUBROUTE.MIGRATE_OPS_DOCS} />
            <ProjectSubMenuListItem
                projectId={projectId}
                icon={<AIAgentsIcon size={14} />}
                label={t("layout:navigation.aiAgents")}
                route={PROJECT_SUBROUTE.AI_AGENTS}
            />
            <ProjectSubMenuListItem
                projectId={projectId}
                icon={<ApiReferenceIcon size={14} />}
                label={t("layout:navigation.apiAndCli")}
                route={PROJECT_SUBROUTE.PROGRAMMATIC_ACCESS}
            />
        </>
    );
});

// ======================
// IntegrationsSubMenu
// ======================

interface IntegrationsSubMenuProps {}

export const IntegrationsSubMenu: React.FC<IntegrationsSubMenuProps> = observer((p) => {
    const currentProject = useCurrentProject();
    const { t } = useTranslation();

    const projectId = currentProject.getInfo().getProjectId();
    return (
        <>
            <ProjectSubMenuListItem projectId={projectId} label={t("layout:navigation.goodies")} route={PROJECT_SUBROUTE.INTEGRATIONS} default />
            <ProjectSubMenuListItem
                projectId={projectId}
                label={t("layout:navigation.enabled")}
                route={PROJECT_SUBROUTE.INTEGRATIONS}
                subroute={INTEGRATIONS_SUBROUTE.ENABLED}
            />
        </>
    );
});

// ======================
// ManagementSubMenu
// ======================

interface ManagementSubMenuProps {}

export const ManagementSubMenu: React.FC<ManagementSubMenuProps> = (p) => {
    const projectId = useCurrentProjectID();
    const { t } = useTranslation();
    return (
        <>
            <Stack pl={SUBTITLE_LEFT_PADDING} direction={"row"} spacing={1} alignItems={"center"}>
                <ProjectSettingsIcon size={ICON_SIZE} />
                <Typography color={"textPrimary"} variant={"overline"}>
                    {t("layout:navigation.management")}
                </Typography>
            </Stack>
            <ProjectSubMenuListItem
                projectId={projectId}
                icon={<IntegrationsIcon size={14} />}
                label={t("layout:navigation.integrations")}
                route={PROJECT_SUBROUTE.INTEGRATIONS}
                submenu={<IntegrationsSubMenu />}
            />
            <ProjectSubMenuListItem
                projectId={projectId}
                icon={<ReportIcon size={14} />}
                label={t("layout:navigation.reports")}
                route={PROJECT_SUBROUTE.REPORTS}
            />
            <PrivateEditionView>
                <ProjectSubMenuListItem
                    projectId={projectId}
                    icon={<ServerIcon size={14} />}
                    label={t("layout:navigation.peAppliance")}
                    route={PROJECT_SUBROUTE.PE_APPLIANCE}
                />
            </PrivateEditionView>
            <ProjectSubMenuListItem
                projectId={projectId}
                // icon={<ProjectSettingsIcon />}
                label={t("layout:navigation.settings")}
                route={PROJECT_SUBROUTE.SETTINGS}
            />
        </>
    );
};

// ======================
// ProjectSubMenuListItem
// ======================
interface SideMenuListItemProps {
    projectId: string;
    label: string | React.ReactNode;
    icon?: React.ReactNode;
    route?: string;
    subroute?: string;
    submenu?: React.ReactNode;
    default?: boolean;
    forceOpen?: boolean;
    externalLink?: string;
    // Child path appended to the link only; selection still matches the whole route
    // (e.g. Protected Hosts links to `/dr/protected-workloads` but highlights on `/dr/*`).
    linkSubpath?: string;
    // Narrow selection to one child subtree, or carve a sibling's subtree out of it, so two
    // flat items can share a route without both highlighting.
    selectionSubpath?: string;
    excludeSelectionSubpath?: string;
}

const ProjectSubMenuListItem: React.FC<SideMenuListItemProps> = observer((p) => {
    const setAppSubmenu = useAppGlobalState((s) => s.setAppSubmenu);
    const currentAppSubmenu = useAppGlobalState((s) => s.appSubmenu);
    const routeString = p.subroute ? `${p.route}/${p.subroute}` : p.route;
    const routeTemplate = getProjectSubRouteTemplate(routeString);
    const { pathname } = useLocation();
    const selectionTemplate = p.selectionSubpath ? `${routeTemplate}/${p.selectionSubpath}` : routeTemplate;
    const excludedTemplate = p.excludeSelectionSubpath ? `${routeTemplate}/${p.excludeSelectionSubpath}` : undefined;
    const selected =
        p.default || p.subroute
            ? !!matchPath(selectionTemplate, pathname)
            : !!matchPath(selectionTemplate + "/*", pathname) && !(excludedTemplate && matchPath(excludedTemplate + "/*", pathname));
    const link = generatePath(p.linkSubpath ? `${routeTemplate}/${p.linkSubpath}` : routeTemplate, { projectId: p.projectId });
    const navFunction = p.externalLink ? () => window.open(p.externalLink, "_blank") : null;

    const onClick = () => {
        setAppSubmenu(p.route);
    };

    const style = useSideMenuListItemStyle(!!p.subroute || !!p.default);

    return (
        <>
            <ListItemButton component={Link} to={p.externalLink ? null : link} onClick={navFunction || onClick} selected={selected} sx={style}>
                <Box pl={p.subroute || p.default ? 2 : 0} display={"flex"} alignItems={"center"}>
                    {(p.subroute || p.default) && (
                        <>
                            <GoDotFill />
                            &nbsp; &nbsp;
                        </>
                    )}
                    <ListItemText
                        primary={
                            <Typography fontSize={14} fontWeight={"inherit"}>
                                {p.label}
                            </Typography>
                        }
                    />
                </Box>
                {p.icon && (
                    <ListItemIcon
                        sx={{
                            minWidth: "30px",
                            color: "inherit",
                            pl: 1,
                        }}
                    >
                        {p.icon}
                    </ListItemIcon>
                )}
            </ListItemButton>
            {(currentAppSubmenu === p.route || p.forceOpen) && p.submenu}
        </>
    );
});

const ICON_SIZE = 18;
const SUBTITLE_LEFT_PADDING = 2;
